import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  AlertTriangle,
  ArrowLeft,
  CheckCircle,
  Layers,
  MapPin,
  Pencil,
  RefreshCw,
  Save,
  Search,
  X,
} from 'lucide-react';
import MainService from '../../services/MainService';

const emptyFilter = () => ({
  Name: '',
  EmirateId: '',
  AreaId: '',
});

const emptyApply = () => ({
  EmirateId: '',
  AreaId: '',
});

const parseList = (response) => {
  if (Array.isArray(response)) return response;
  if (Array.isArray(response?.Data)) return response.Data;
  if (Array.isArray(response?.data)) return response.data;
  return [];
};

const controlClass =
  'h-10 w-full rounded-lg border border-gray-200 bg-white px-3 text-sm text-gray-900 shadow-sm transition placeholder:text-gray-400 focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20';

const tableInputClass =
  'w-full rounded-md border border-transparent bg-transparent px-2 py-1.5 text-sm text-gray-900 transition hover:border-gray-200 focus:border-primary focus:bg-white focus:outline-none focus:ring-2 focus:ring-primary/15';

const sameId = (a, b) => String(a ?? '') === String(b ?? '');

const isRowModified = (row) => {
  const o = row._orig;
  if (!o) return false;
  return (
    (row.Name || '') !== (o.Name || '') ||
    (row.NameAr || '') !== (o.NameAr || '') ||
    !sameId(row.EmirateId, o.EmirateId) ||
    !sameId(row.AreaId, o.AreaId)
  );
};

/**
 * Bulk location edit — FilterLocations / saveBulkLocations, modern portal UI.
 */
const LocationBulkEdit = () => {
  const navigate = useNavigate();
  const [filter, setFilter] = useState(emptyFilter);
  const [apply, setApply] = useState(emptyApply);
  const [locations, setLocations] = useState([]);
  const [emirates, setEmirates] = useState([]);
  const [areas, setAreas] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [hasLoaded, setHasLoaded] = useState(false);
  const [tableSearch, setTableSearch] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const emirateNameById = useMemo(() => {
    const map = {};
    emirates.forEach((e) => {
      map[String(e.Id)] = e.Name || '';
    });
    return map;
  }, [emirates]);

  const areaNameById = useMemo(() => {
    const map = {};
    areas.forEach((a) => {
      map[String(a.Id)] = a.Name || '';
    });
    return map;
  }, [areas]);

  const loadLookups = useCallback(async () => {
    try {
      const [emirateRes, areaRes] = await Promise.all([
        MainService.EmiratesInDropDown(),
        MainService.AreasInDropDown(),
      ]);
      setEmirates(parseList(emirateRes));
      setAreas(parseList(areaRes));
    } catch (err) {
      console.error('Load location edit lookups error:', err);
      setError('Failed to load emirates / areas');
    }
  }, []);

  useEffect(() => {
    loadLookups();
  }, [loadLookups]);

  useEffect(() => {
    if (!error) return undefined;
    const timer = setTimeout(() => setError(''), 5000);
    return () => clearTimeout(timer);
  }, [error]);

  useEffect(() => {
    if (!success) return undefined;
    const timer = setTimeout(() => setSuccess(''), 4000);
    return () => clearTimeout(timer);
  }, [success]);

  const clearWorkspace = ({ keepSuccess = false } = {}) => {
    setFilter(emptyFilter());
    setApply(emptyApply());
    setLocations([]);
    setTableSearch('');
    setHasLoaded(false);
    setError('');
    if (!keepSuccess) setSuccess('');
  };

  const filterLocations = async () => {
    try {
      setLoading(true);
      setError('');
      setSuccess('');
      const response = await MainService.FilterLocations({
        Name: (filter.Name || '').trim(),
        EmirateId: filter.EmirateId ? Number(filter.EmirateId) : 0,
        AreaId: filter.AreaId ? Number(filter.AreaId) : 0,
      });
      const list = parseList(response).map((loc) => {
        const Name = loc.Name || '';
        const NameAr = loc.NameAr || '';
        const EmirateId = loc.EmirateId || '';
        const AreaId = loc.AreaId || '';
        return {
          Id: loc.Id,
          Name,
          NameAr,
          EmirateId,
          AreaId,
          LocationUrl: loc.LocationUrl || '',
          LAT: loc.LAT || '',
          LNG: loc.LNG || '',
          _orig: { Name, NameAr, EmirateId, AreaId },
        };
      });
      setLocations(list);
      setTableSearch('');
      setHasLoaded(true);
      if (list.length === 0) {
        setError('No locations found for this filter');
      }
    } catch (err) {
      console.error('Filter locations error:', err);
      setLocations([]);
      setHasLoaded(true);
      setError('Network Error');
    } finally {
      setLoading(false);
    }
  };

  const updateRow = (id, field, value) => {
    setLocations((prev) =>
      prev.map((row) => (row.Id === id ? { ...row, [field]: value } : row))
    );
  };

  const applyEmirates = () => {
    if (!apply.EmirateId) {
      setError('Select an emirate to apply');
      return;
    }
    if (locations.length === 0) {
      setError('Load locations first');
      return;
    }
    const emirateId = Number(apply.EmirateId);
    const changed = locations.filter((row) => !sameId(row.EmirateId, emirateId)).length;
    setLocations((prev) =>
      prev.map((row) => (sameId(row.EmirateId, emirateId) ? row : { ...row, EmirateId: emirateId }))
    );
    setSuccess(
      changed > 0
        ? `Emirate applied to ${changed} location(s)`
        : 'All rows already have this emirate'
    );
  };

  const applyAreas = () => {
    if (!apply.AreaId) {
      setError('Select an area to apply');
      return;
    }
    if (locations.length === 0) {
      setError('Load locations first');
      return;
    }
    const areaId = Number(apply.AreaId);
    const changed = locations.filter((row) => !sameId(row.AreaId, areaId)).length;
    setLocations((prev) =>
      prev.map((row) => (sameId(row.AreaId, areaId) ? row : { ...row, AreaId: areaId }))
    );
    setSuccess(
      changed > 0
        ? `Area applied to ${changed} location(s)`
        : 'All rows already have this area'
    );
  };

  const modifiedLocations = locations.filter(isRowModified);
  const modifiedCount = modifiedLocations.length;

  const visibleLocations = useMemo(() => {
    const q = tableSearch.trim().toLowerCase();
    if (!q) return locations;
    return locations.filter((row) => {
      const emirate = (emirateNameById[String(row.EmirateId)] || '').toLowerCase();
      const area = (areaNameById[String(row.AreaId)] || '').toLowerCase();
      return (
        (row.Name || '').toLowerCase().includes(q) ||
        (row.NameAr || '').toLowerCase().includes(q) ||
        emirate.includes(q) ||
        area.includes(q) ||
        String(row.Id).includes(q)
      );
    });
  }, [locations, tableSearch, emirateNameById, areaNameById]);

  const saveLocations = async () => {
    if (locations.length === 0) {
      setError('No locations to save. Search first.');
      return;
    }
    if (modifiedCount === 0) {
      setError('No changes to save');
      return;
    }

    const count = modifiedCount;
    try {
      setSaving(true);
      setError('');
      const payload = modifiedLocations.map((loc) => ({
        Id: loc.Id,
        Name: loc.Name,
        NameAr: loc.NameAr,
        EmirateId: Number(loc.EmirateId) || 0,
        AreaId: Number(loc.AreaId) || 0,
        LocationUrl: loc.LocationUrl || '',
      }));

      const response = await MainService.SaveBulkLocations(payload);
      if (response === true || response === 'true' || response === 'Success') {
        clearWorkspace({ keepSuccess: true });
        setSuccess(`${count} location(s) saved successfully`);
      } else {
        setError('Something went wrong while saving');
      }
    } catch (err) {
      console.error('Save bulk locations error:', err);
      setError('Network Error');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="relative space-y-4 pb-28">
      {error && (
        <div className="fixed top-4 right-4 z-50 flex min-w-[280px] max-w-md items-center gap-3 rounded-xl border border-red-200 bg-white px-4 py-3 text-red-700 shadow-xl">
          <AlertTriangle className="h-5 w-5 flex-shrink-0" />
          <span className="flex-1 text-sm font-medium">{error}</span>
          <button type="button" onClick={() => setError('')} className="text-red-400 hover:text-red-600">
            <X className="h-4 w-4" />
          </button>
        </div>
      )}
      {success && (
        <div className="fixed top-4 right-4 z-50 flex min-w-[280px] max-w-md items-center gap-3 rounded-xl border border-emerald-200 bg-white px-4 py-3 text-emerald-700 shadow-xl">
          <CheckCircle className="h-5 w-5 flex-shrink-0" />
          <span className="flex-1 text-sm font-medium">{success}</span>
          <button type="button" onClick={() => setSuccess('')} className="text-emerald-400 hover:text-emerald-600">
            <X className="h-4 w-4" />
          </button>
        </div>
      )}

      {/* Compact page header */}
      <div className="flex items-center justify-between gap-3">
        <div className="flex min-w-0 items-center gap-3">
          <button
            type="button"
            onClick={() => navigate('/home/masters/locations')}
            className="inline-flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-lg border border-gray-200 bg-white text-gray-600 transition hover:border-gray-300 hover:bg-gray-50 hover:text-gray-900"
            title="Back to Locations"
          >
            <ArrowLeft className="h-4 w-4" />
          </button>
          <div className="min-w-0">
            <h1 className="truncate text-xl font-bold text-gray-900 sm:text-2xl">Bulk edit locations</h1>
            <p className="hidden text-sm text-gray-500 sm:block">Search, edit, apply emirate/area, then save.</p>
          </div>
        </div>
        {hasLoaded && (
          <div className="flex flex-shrink-0 items-center gap-1.5 rounded-full bg-[#46955A]/10 px-3 py-1 text-xs font-semibold text-[#46955A] sm:text-sm">
            <MapPin className="h-3.5 w-3.5" />
            {locations.length}
          </div>
        )}
      </div>

      {/* Main card: search + apply + table */}
      <div className="overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-sm">
        {/* Search toolbar — fields + Clear/Search always one row on md+ */}
        <div className="border-b border-gray-100 bg-slate-50/80 px-4 py-4 sm:px-5">
          <div className="flex flex-col gap-3 xl:flex-row xl:items-end">
            <div className="grid min-w-0 flex-1 grid-cols-1 gap-3 sm:grid-cols-3">
              <div>
                <label className="mb-1 block text-[11px] font-semibold uppercase tracking-wider text-gray-500">
                  Name
                </label>
                <div className="relative">
                  <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
                  <input
                    value={filter.Name}
                    onChange={(e) => setFilter((prev) => ({ ...prev, Name: e.target.value }))}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        filterLocations();
                      }
                    }}
                    className={`${controlClass} pl-9`}
                    placeholder="Search by name…"
                  />
                </div>
              </div>
              <div>
                <label className="mb-1 block text-[11px] font-semibold uppercase tracking-wider text-gray-500">
                  Emirate
                </label>
                <select
                  value={filter.EmirateId}
                  onChange={(e) => setFilter((prev) => ({ ...prev, EmirateId: e.target.value }))}
                  className={controlClass}
                >
                  <option value="">Any emirate</option>
                  {emirates.map((e) => (
                    <option key={e.Id} value={e.Id}>{e.Name}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="mb-1 block text-[11px] font-semibold uppercase tracking-wider text-gray-500">
                  Area
                </label>
                <select
                  value={filter.AreaId}
                  onChange={(e) => setFilter((prev) => ({ ...prev, AreaId: e.target.value }))}
                  className={controlClass}
                >
                  <option value="">Any area</option>
                  {areas.map((a) => (
                    <option key={a.Id} value={a.Id}>{a.Name}</option>
                  ))}
                </select>
              </div>
            </div>

            {/* Clear + Search always side by side */}
            <div className="flex flex-shrink-0 flex-row items-stretch gap-2 sm:items-end">
              <button
                type="button"
                onClick={() => clearWorkspace()}
                disabled={loading || saving}
                className="inline-flex h-10 flex-1 items-center justify-center gap-1.5 rounded-lg border border-gray-200 bg-white px-4 text-sm font-semibold text-gray-700 shadow-sm transition hover:bg-gray-50 disabled:opacity-50 sm:flex-none sm:min-w-[6.5rem]"
              >
                <RefreshCw className="h-4 w-4" />
                Clear
              </button>
              <button
                type="button"
                onClick={filterLocations}
                disabled={loading || saving}
                className="inline-flex h-10 flex-1 items-center justify-center gap-1.5 rounded-lg bg-primary px-5 text-sm font-semibold text-white shadow-sm transition hover:bg-green-700 disabled:opacity-50 sm:flex-none sm:min-w-[7.5rem]"
              >
                {loading ? <RefreshCw className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4" />}
                Search
              </button>
            </div>
          </div>
        </div>

        {/* Bulk apply strip */}
        {locations.length > 0 && (
          <div className="flex flex-col gap-3 border-b border-gray-100 bg-[#46955A]/[0.06] px-4 py-3 sm:flex-row sm:items-center sm:px-5">
            <div className="flex items-center gap-2 text-sm font-semibold text-gray-800 sm:min-w-[9rem]">
              <Layers className="h-4 w-4 text-primary" />
              Apply to all
            </div>
            <div className="flex min-w-0 flex-1 flex-col gap-2 sm:flex-row sm:items-center">
              <select
                value={apply.EmirateId}
                onChange={(e) => setApply((prev) => ({ ...prev, EmirateId: e.target.value }))}
                className={`${controlClass} sm:max-w-[14rem]`}
              >
                <option value="">Emirate…</option>
                {emirates.map((e) => (
                  <option key={e.Id} value={e.Id}>{e.Name}</option>
                ))}
              </select>
              <button
                type="button"
                onClick={applyEmirates}
                disabled={saving}
                className="inline-flex h-10 items-center justify-center rounded-lg border border-primary/25 bg-white px-3 text-sm font-semibold text-primary transition hover:bg-primary hover:text-white disabled:opacity-50"
              >
                Apply emirate
              </button>
              <div className="hidden h-6 w-px bg-gray-300 sm:block" />
              <select
                value={apply.AreaId}
                onChange={(e) => setApply((prev) => ({ ...prev, AreaId: e.target.value }))}
                className={`${controlClass} sm:max-w-[14rem]`}
              >
                <option value="">Area…</option>
                {areas.map((a) => (
                  <option key={a.Id} value={a.Id}>{a.Name}</option>
                ))}
              </select>
              <button
                type="button"
                onClick={applyAreas}
                disabled={saving}
                className="inline-flex h-10 items-center justify-center rounded-lg border border-primary/25 bg-white px-3 text-sm font-semibold text-primary transition hover:bg-primary hover:text-white disabled:opacity-50"
              >
                Apply area
              </button>
            </div>
          </div>
        )}

        {/* Table / empty */}
        {loading ? (
          <div className="flex flex-col items-center justify-center gap-3 py-24 text-gray-500">
            <RefreshCw className="h-9 w-9 animate-spin text-primary" />
            <p className="text-sm font-medium">Loading locations…</p>
          </div>
        ) : !hasLoaded ? (
          <div className="flex flex-col items-center justify-center gap-2 px-6 py-20 text-center">
            <div className="mb-1 flex h-12 w-12 items-center justify-center rounded-xl bg-slate-100 text-slate-400">
              <Search className="h-6 w-6" />
            </div>
            <p className="text-base font-semibold text-gray-800">Search to get started</p>
            <p className="max-w-xs text-sm text-gray-500">
              Filter by name, emirate, or area — then edit rows and save.
            </p>
          </div>
        ) : locations.length === 0 ? (
          <div className="px-6 py-16 text-center text-sm text-gray-500">
            No locations matched. Adjust filters and search again.
          </div>
        ) : (
          <>
            <div className="flex flex-col gap-2 border-b border-gray-100 px-4 py-3 sm:flex-row sm:items-center sm:justify-between sm:px-5">
              <div className="relative min-w-0 flex-1 sm:max-w-sm">
                <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
                <input
                  value={tableSearch}
                  onChange={(e) => setTableSearch(e.target.value)}
                  className={`${controlClass} pl-9 pr-9`}
                  placeholder="Search in table (name, Arabic, emirate, area)…"
                />
                {tableSearch && (
                  <button
                    type="button"
                    onClick={() => setTableSearch('')}
                    className="absolute right-2 top-1/2 -translate-y-1/2 rounded p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600"
                    title="Clear table search"
                  >
                    <X className="h-4 w-4" />
                  </button>
                )}
              </div>
              <p className="text-xs text-gray-500 sm:text-sm">
                Showing{' '}
                <span className="font-semibold text-gray-800">{visibleLocations.length}</span>
                {' '}of {locations.length}
                {modifiedCount > 0 && (
                  <span className="ml-2 inline-flex items-center gap-1 rounded-full bg-amber-100 px-2 py-0.5 text-[11px] font-semibold text-amber-800">
                    <Pencil className="h-3 w-3" />
                    {modifiedCount} edited
                  </span>
                )}
              </p>
            </div>

            {visibleLocations.length === 0 ? (
              <div className="px-6 py-14 text-center text-sm text-gray-500">
                No rows match “{tableSearch.trim()}”. Clear the table search to see all loaded locations.
              </div>
            ) : (
              <div className="master-table-scroll max-h-[min(58vh,680px)] overflow-auto">
                <table className="w-full min-w-[880px] text-left text-sm">
                  <thead className="sticky top-0 z-10 border-b border-gray-200 bg-white">
                    <tr>
                      <th className="w-12 px-4 py-3 text-[11px] font-semibold uppercase tracking-wider text-gray-400">#</th>
                      <th className="w-16 px-2 py-3 text-[11px] font-semibold uppercase tracking-wider text-gray-400" />
                      <th className="px-2 py-3 text-[11px] font-semibold uppercase tracking-wider text-gray-400">Name</th>
                      <th className="px-2 py-3 text-[11px] font-semibold uppercase tracking-wider text-gray-400">Name Ar</th>
                      <th className="w-[18%] px-2 py-3 text-[11px] font-semibold uppercase tracking-wider text-gray-400">Emirate</th>
                      <th className="w-[18%] px-2 py-3 text-[11px] font-semibold uppercase tracking-wider text-gray-400">Area</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-50">
                    {visibleLocations.map((row, index) => {
                      const edited = isRowModified(row);
                      return (
                        <tr
                          key={row.Id}
                          className={`transition-colors ${
                            edited
                              ? 'border-l-4 border-l-amber-400 bg-amber-50/90 hover:bg-amber-50'
                              : 'border-l-4 border-l-transparent hover:bg-[#46955A]/[0.04]'
                          }`}
                        >
                          <td className="px-4 py-2.5 align-middle text-xs font-medium tabular-nums text-gray-400">
                            {index + 1}
                          </td>
                          <td className="px-1 py-2.5 align-middle">
                            {edited ? (
                              <span
                                className="inline-flex items-center gap-0.5 rounded-full bg-amber-200/80 px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wide text-amber-900"
                                title="This row was edited"
                              >
                                <Pencil className="h-3 w-3" />
                                Edit
                              </span>
                            ) : null}
                          </td>
                          <td className="px-2 py-1.5 align-middle">
                            <input
                              type="text"
                              value={row.Name}
                              onChange={(e) => updateRow(row.Id, 'Name', e.target.value)}
                              className={tableInputClass}
                            />
                          </td>
                          <td className="px-2 py-1.5 align-middle">
                            <input
                              type="text"
                              value={row.NameAr}
                              onChange={(e) => updateRow(row.Id, 'NameAr', e.target.value)}
                              className={tableInputClass}
                              dir="rtl"
                            />
                          </td>
                          <td className="px-2 py-1.5 align-middle">
                            <select
                              value={row.EmirateId}
                              onChange={(e) => updateRow(row.Id, 'EmirateId', e.target.value)}
                              className={tableInputClass}
                            >
                              <option value="">Select</option>
                              {emirates.map((e) => (
                                <option key={e.Id} value={e.Id}>{e.Name}</option>
                              ))}
                            </select>
                          </td>
                          <td className="px-2 py-1.5 align-middle">
                            <select
                              value={row.AreaId}
                              onChange={(e) => updateRow(row.Id, 'AreaId', e.target.value)}
                              className={tableInputClass}
                            >
                              <option value="">Select</option>
                              {areas.map((a) => (
                                <option key={a.Id} value={a.Id}>{a.Name}</option>
                              ))}
                            </select>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </>
        )}
      </div>

      {/* Sticky save bar */}
      {locations.length > 0 && (
        <div className="fixed bottom-0 left-0 right-0 z-40 border-t border-gray-200 bg-white/95 px-4 py-3 shadow-[0_-6px_24px_rgba(0,0,0,0.06)] backdrop-blur sm:px-6">
          <div className="mx-auto flex max-w-7xl items-center justify-between gap-3">
            <p className="text-sm text-gray-600">
              <span className="font-semibold text-gray-900">{modifiedCount}</span>
              {' '}changed of {locations.length} loaded
            </p>
            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => clearWorkspace()}
                disabled={loading || saving}
                className="inline-flex h-10 items-center justify-center gap-2 rounded-lg border border-gray-200 bg-white px-4 text-sm font-semibold text-gray-700 hover:bg-gray-50 disabled:opacity-50"
              >
                Discard
              </button>
              <button
                type="button"
                onClick={saveLocations}
                disabled={loading || saving || modifiedCount === 0}
                className="inline-flex h-10 min-w-[10rem] items-center justify-center gap-2 rounded-lg bg-primary px-5 text-sm font-semibold text-white shadow-sm hover:bg-green-700 disabled:opacity-50"
              >
                {saving ? <RefreshCw className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
                {saving ? 'Saving…' : modifiedCount > 0 ? `Save ${modifiedCount}` : 'Save'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default LocationBulkEdit;
